import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Shared parallax registry.
 *
 * Elements register once; a single rAF loop measures the ones currently near
 * the viewport and writes a `--p` custom property (0..1 progress) plus a
 * translated transform. Writing CSS variables instead of re-rendering keeps
 * Vue completely out of the scroll path.
 */
const items = new Set()
let running = false
let gate = null

function ensureGate() {
  if (gate || typeof IntersectionObserver === 'undefined') return gate
  gate = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const item = [...items].find((i) => i.el === e.target)
        if (item) item.near = e.isIntersecting
      }
    },
    { rootMargin: '30% 0px 30% 0px' }
  )
  return gate
}

function tick() {
  const vh = window.innerHeight
  for (const item of items) {
    if (!item.near || !item.el.isConnected) continue
    const r = item.el.getBoundingClientRect()
    const total = r.height + vh
    const p = Math.min(1, Math.max(0, (vh - r.top) / total))
    item.el.style.setProperty('--p', p.toFixed(4))
    if (item.speed) {
      // centre the motion so the element sits on its natural spot mid-screen
      const shift = (p - 0.5) * item.speed * -100
      item.el.style.setProperty('--shift', shift.toFixed(2) + 'px')
    }
    if (item.onFrame) item.onFrame(p)
  }
  requestAnimationFrame(tick)
}

function start() {
  if (running) return
  running = true
  requestAnimationFrame(tick)
}

/**
 * @param {number} speed  positive = drifts up faster than scroll (foreground),
 *                        negative = lags behind (background)
 * @param {(p:number)=>void} onFrame optional per-frame callback with progress
 */
export function useParallax(speed = 0, onFrame = null) {
  const el = ref(null)
  let item = null

  onMounted(() => {
    if (!el.value || typeof window === 'undefined') return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    item = { el: el.value, speed: reduced ? 0 : speed, onFrame, near: true }
    items.add(item)
    const g = ensureGate()
    if (g) {
      item.near = false
      g.observe(el.value)
    }
    start()
  })

  onUnmounted(() => {
    if (!item) return
    if (gate && item.el) gate.unobserve(item.el)
    items.delete(item)
  })

  return el
}
